import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { packagingSplitOf, describePackagingSplit, shortPackagingSplit } from './linePackaging';

/**
 * Note di lavorazione per riga prodotto dell'offerta (richiesta Say Farm).
 *
 * Stanno in deal_services.metadata.processing_note: nessuna migration, e il
 * metadata si salva gia' in creazione, modifica e duplicazione dell'offerta.
 * Il magazzino le legge dall'offerta attraverso l'ordine (order_items.deal_service_id,
 * oppure offerta dell'ordine + prodotto per gli ordini senza collegamento di riga),
 * come le indicazioni per il magazzino di DealWarehouseInfoCard: una sola fonte,
 * e una correzione sull'offerta si vede subito sull'ordine.
 *
 * NON vanno in order_items.notes: quel campo si stampa nella conferma d'ordine
 * per il cliente (ManualOrderPrint). Non compaiono nemmeno nel preventivo.
 *
 * Funzione per organizzazione: settings.warehouse_settings.line_processing_notes_enabled.
 */

export const PROCESSING_NOTE_KEY = 'processing_note';

/** Nota di lavorazione della riga ('' se assente). */
export function processingNoteOf(metadata: unknown): string {
  const v = (metadata as Record<string, unknown> | null | undefined)?.[PROCESSING_NOTE_KEY];
  return typeof v === 'string' ? v.trim() : '';
}

/** Metadata con la nota aggiornata; nota vuota = chiave tolta. */
export function withProcessingNote(metadata: unknown, note: string): Record<string, unknown> {
  const next: Record<string, unknown> = { ...((metadata as Record<string, unknown> | null | undefined) || {}) };
  if (note.trim()) next[PROCESSING_NOTE_KEY] = note;
  else delete next[PROCESSING_NOTE_KEY];
  return next;
}

export interface OrderProcessingNotes {
  /** Nota per riga d'ordine (order_items.id) */
  byOrderItem: Record<string, string>;
  /** Note per prodotto (le righe di preparazione sono raggruppate per prodotto) */
  byProduct: Record<string, string>;
  /** Confezionamento per riga d'ordine, «5 confezioni da 1 kg» (linePackaging.ts) */
  packagingByOrderItem: Record<string, string>;
  /** Confezionamento per riga d'ordine in forma breve per la stampa, «5 × 1 kg» */
  packagingShortByOrderItem: Record<string, string>;
  /** Confezionamento per prodotto (preparazione) */
  packagingByProduct: Record<string, string>;
  /** Numero di confezioni per riga d'ordine (etichette proposte nella stampa dall'ordine) */
  packCountByOrderItem: Record<string, number>;
}

const EMPTY: OrderProcessingNotes = { byOrderItem: {}, byProduct: {}, packagingByOrderItem: {}, packagingShortByOrderItem: {}, packagingByProduct: {}, packCountByOrderItem: {} };

export async function fetchOrderProcessingNotes(orderId: string): Promise<OrderProcessingNotes> {
  const { data: order } = await supabase.from('orders').select('deal_id').eq('id', orderId).maybeSingle();
  const dealId = (order as { deal_id: string | null } | null)?.deal_id;
  if (!dealId) return EMPTY;

  const [itemsRes, linesRes] = await Promise.all([
    // '*': deal_service_id puo' mancare dove la migration non c'e'
    supabase.from('order_items').select('*').eq('order_id', orderId),
    supabase.from('deal_services').select('id, product_id, metadata, unit_of_measure').eq('deal_id', dealId).eq('line_type', 'product'),
  ]);
  if (itemsRes.error || linesRes.error) return EMPTY;

  const noteByLine: Record<string, string> = {};
  const notesByDealProduct: Record<string, string[]> = {};
  const packByLine: Record<string, { long: string; short: string; count: number }> = {};
  const packsByDealProduct: Record<string, Array<{ long: string; short: string; count: number }>> = {};
  for (const l of (linesRes.data || []) as Array<{ id: string; product_id: string | null; metadata: unknown; unit_of_measure?: string | null }>) {
    const split = packagingSplitOf(l.metadata);
    if (split) {
      const p = { long: describePackagingSplit(split, l.unit_of_measure), short: shortPackagingSplit(split, l.unit_of_measure), count: split.packs };
      packByLine[l.id] = p;
      if (l.product_id) (packsByDealProduct[l.product_id] ||= []).push(p);
    }
    const note = processingNoteOf(l.metadata);
    if (!note) continue;
    noteByLine[l.id] = note;
    if (l.product_id) (notesByDealProduct[l.product_id] ||= []).push(note);
  }

  const byOrderItem: Record<string, string> = {};
  const productNotes: Record<string, string[]> = {};
  const packagingByOrderItem: Record<string, string> = {};
  const packagingShortByOrderItem: Record<string, string> = {};
  const productPacks: Record<string, string[]> = {};
  const packCountByOrderItem: Record<string, number> = {};
  for (const it of (itemsRes.data || []) as Array<{ id: string; product_id: string | null; deal_service_id?: string | null }>) {
    const packs = it.deal_service_id
      ? (packByLine[it.deal_service_id] ? [packByLine[it.deal_service_id]] : [])
      : it.product_id ? (packsByDealProduct[it.product_id] || []) : [];
    if (packs.length) {
      packagingByOrderItem[it.id] = [...new Set(packs.map(p => p.long))].join(' · ');
      packagingShortByOrderItem[it.id] = [...new Set(packs.map(p => p.short))].join(' · ');
      packCountByOrderItem[it.id] = packs.reduce((n, p) => n + p.count, 0);
      if (it.product_id) (productPacks[it.product_id] ||= []).push(...packs.map(p => p.long));
    }
    const note = it.deal_service_id
      ? noteByLine[it.deal_service_id]
      : it.product_id ? [...new Set(notesByDealProduct[it.product_id] || [])].join(' · ') : '';
    if (!note) continue;
    byOrderItem[it.id] = note;
    if (it.product_id) (productNotes[it.product_id] ||= []).push(note);
  }
  const byProduct = Object.fromEntries(
    Object.entries(productNotes).map(([pid, notes]) => [pid, [...new Set(notes)].join(' · ')]),
  );
  const packagingByProduct = Object.fromEntries(
    Object.entries(productPacks).map(([pid, list]) => [pid, [...new Set(list)].join(' · ')]),
  );
  return { byOrderItem, byProduct, packagingByOrderItem, packagingShortByOrderItem, packagingByProduct, packCountByOrderItem };
}

/** Note di lavorazione dell'ordine; vuote se la funzione e' spenta o l'ordine non viene da un'offerta. */
export function useOrderProcessingNotes(orderId: string | null | undefined, enabled: boolean): OrderProcessingNotes {
  const [notes, setNotes] = useState<OrderProcessingNotes>(EMPTY);
  useEffect(() => {
    setNotes(EMPTY);
    if (!enabled || !orderId) return;
    let cancelled = false;
    fetchOrderProcessingNotes(orderId)
      .then((n) => { if (!cancelled) setNotes(n); })
      .catch(() => { /* senza note l'ordine resta leggibile */ });
    return () => { cancelled = true; };
  }, [orderId, enabled]);
  return notes;
}
